import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { TimeSlot } from '@adornis/buchungsheld-db/datetime/time-slot.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import * as m from '../../../../localization/out/messages.js';
import { getJobPositionByID } from '../../../api/job-position.js';
import { BOOK_INTERVIEW_SLOT_RESULT, bookInterviewSlot } from '../../../api/booking.js';
import { getJobApplicationByCurrentUserAndPosition } from '../../../api/job-application.js';
import { JobApplicationStatus } from '../../../db/job-application.js';
import { JobPosition } from '../../../db/job-position.js';
import '../../components/bh-topbar-copy.js';
import { mainRouter } from '../../mainRouter.js';

export const BookAppointmentPage = stateful(({ jobID }: { jobID?: string }) => {
  const jobQuery = useQuery(
    () =>
      jobID
        ? getJobPositionByID(jobID)({
            _id: 1,
            title: 1,
            meetingLink: 1,
            startDate: 1,
            timeSlots: selectionSet(() => TimeSlot, 1),
          })
        : Promise.resolve(null),
    [jobID],
  );
  const jobApplicationQuery = useQuery(
    () =>
      jobID
        ? getJobApplicationByCurrentUserAndPosition(jobID, JobApplicationStatus.ACCEPTED)({ _id: 1 })
        : Promise.resolve(null),
    [jobID],
  );
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [bookingWarning, setBookingWarning] = useState<string | null>(null);

  const groupedByDay = (jobQuery.value?.timeSlots ?? []).reduce(
    (acc, s) => {
      const start = s?.start ? new Date(s.start) : null;
      const end = s?.end ? new Date(s.end) : null;
      if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) return acc;
      const label = format(start, 'dd.MM.yyyy');
      (acc[label] ??= []).push({ id: s._id as string, start, end });
      return acc;
    },
    {} as Record<string, Array<{ id: string; start: Date; end: Date }>>,
  );

  Object.values(groupedByDay).forEach(items => items.sort((a, b) => a.start.getTime() - b.start.getTime()));

  if (!jobID) {
    return html`
      <x-flex class="max-h-full overflow-auto">
        <bh-topbar-copy></bh-topbar-copy>
        <div class="min-h-dvh w-full p-6">
          <div class="max-w-xl mx-auto flex flex-col items-stretch gap-6">
            <div class="card bg-base-100 shadow">
              <div class="card-body">
                <h2 class="card-title text-error">Ungültiger Link</h2>
                <p>
                  Dieser Buchungslink ist leider nicht gültig oder unvollständig. Bitte prüfe den Link oder wende dich
                  an den Support. Gehe außerdem sicher, dass du mit dem gleichen Account (gleiche E-Mail-Adresse)
                  angemeldet bist, mit dem du dich für die Stelle beworben hast.
                </p>
                <div class="card-actions justify-end">
                  <a class="btn" href="/">Zur Startseite</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </x-flex>
    `;
  }

  if (!jobApplicationQuery.value) {
    return html` <x-flex class="max-h-full overflow-auto">
      <bh-topbar-copy></bh-topbar-copy>
      <div class="min-h-dvh w-full p-6">
        <div class="max-w-xl mx-auto flex flex-col items-stretch gap-6">
          <div class="card bg-base-100 shadow">
            <div class="card-body">
              <h2 class="card-title text-error">Falscher Account</h2>
              <p>
                Dieser Buchungslink gehört nicht zu diesem Account. Bitte nutze den Account mit dem die Bewerbung
                durchgeführt wurde.
              </p>
              <div class="card-actions justify-end">
                <a class="btn" href="/">Zur Startseite</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </x-flex>`;
  }

  return html`
    <x-flex class="max-h-full overflow-auto">
      <bh-topbar-copy></bh-topbar-copy>
      <div class="min-h-dvh w-full p-6">
        <div class="max-w-xl mx-auto flex flex-col items-stretch gap-6">
          <div class="card bg-base-100">
            <div class="card-body">
              <p>
                Herzlichen Glückwunsch! Deine Bewerbung für die Stelle <i>${jobQuery.value?.title}</i> hat gut gepasst
                und das Unternehmen möchte dich kennenlernen.
                ${
                  jobQuery.value?.timeSlots?.[0]?.start
                    ? `Das Gespräch wird am
                ${format(new Date(jobQuery.value?.timeSlots?.[0].start), 'dd.MM.yyyy')} stattfinden. `
                    : nothing
                }Hier
                kannst du selbst einen Termin auswählen, der für dich gut passt:
              </p>
            </div>
          </div>

          ${Object.entries(groupedByDay).map(
            ([label, items]) => html`
              <div class="card bg-base-100 shadow">
                <div class="card-body">
                  <h2 class="card-title">Freie Termine für <i>${label}</i></h2>
                  <div class="mt-2 flex flex-col gap-2">
                    ${items.map(
                      s =>
                        html`<label class="label cursor-pointer justify-start gap-3">
                          <input
                            type="radio"
                            name="slot"
                            class="radio"
                            .checked=${selectedTime === s.id}
                            @change=${() => setSelectedTime(s.id)}
                          />
                          <span>${format(s.start, 'HH:mm')} - ${format(s.end, 'HH:mm')}</span>
                        </label>`,
                    )}
                  </div>
                </div>
              </div>
            `,
          )}
          ${
            bookingWarning
              ? html`<div class="alert alert-warning shadow-lg" role="alert">
                  <span class="icon-[material-symbols--warning] text-xl"></span>
                  <span class="font-medium">${bookingWarning}</span>
                </div>`
              : nothing
          }

          <div class="w-full flex justify-center">
            <button
              class="btn btn-primary"
              ?disabled=${!selectedTime || !!bookingWarning}
              @click=${async (e: MouseEvent) => {
                (e.currentTarget as HTMLButtonElement).style.pointerEvents = 'none';
                if (!selectedTime || !jobQuery.value) return;
                try {
                  const job = jobQuery.value as JobPosition;
                  const selectedSlot = Object.values(groupedByDay)
                    .flat()
                    .find(slot => slot.id === selectedTime);
                  if (!selectedSlot) {
                    void XSnackbar.show('Buchung fehlgeschlagen. Bitte erneut versuchen.');
                    return;
                  }
                  const result = await bookInterviewSlot(job._id!, selectedTime)();
                  if (result === BOOK_INTERVIEW_SLOT_RESULT.BOOKED_MAIL_FAILED) {
                    setBookingWarning(
                      m.booking_confirmation_mail_failed({
                        date: format(selectedSlot.start, 'dd.MM.yyyy'),
                        startTime: format(selectedSlot.start, 'HH:mm'),
                        endTime: format(selectedSlot.end, 'HH:mm'),
                      }),
                    );
                  } else if (result !== BOOK_INTERVIEW_SLOT_RESULT.BOOKED) {
                    void XSnackbar.show('Buchung fehlgeschlagen. Bitte erneut versuchen.');
                  } else {
                    void XSnackbar.show('Termin erfolgreich gebucht.');
                    mainRouter.goTo('candidate');
                  }
                } catch (e: any) {
                  console.error('Failed to book slot', e);
                  void XSnackbar.show(e?.message ?? 'Buchung fehlgeschlagen. Bitte erneut versuchen.');
                }
              }}
            >
              Jetzt Termin verbindlich buchen
            </button>
          </div>
        </div>
      </div>
    </x-flex>
  `;
});
